import {useTemplatesByCompany} from '@api'
import {getUserData, isCompanyUser, renderClickableCell, useViewNavigate} from '@common'
import {NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef} from '@mui/x-data-grid'
import {pageLinksAtom, getUserSelectionAtom} from '@state'

import {useEffect, useMemo} from 'react'

import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

export interface ContractTemplateTableData {
  name: string
  description: string
  id: string
  contractType: string
}

export function useContractTemplateSearchModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const {t} = useTranslation()
  const navigate = useViewNavigate()

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const {data} = useTemplatesByCompany(userSelection?.companyId)

  const rows = useMemo(() => {
    if (!data) return []
    else
      return data.map(item => ({
        name: item.name,
        description: item.description,
        id: item.id,
        contractType: item.contractType,
      }))
  }, [data])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Contracts), link: NavigationPathKey.CONTRACT_DASHBOARD},
        {title: t(TranslationKey['Contract template search']), link: NavigationPathKey.CONTRACT_TEMPLATE_SEARCH},
      ]),
    [],
  )

  /**
   * Create button should only be available to Administrator users
   */
  const showCreateButton = !isCompanyUser()

  /**
   * Contract Type table columns configuration
   */
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 0.8,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.CONTRACT_TEMPLATE_VIEW}/${params.row.id}`)),
    },
    {field: 'description', headerName: t(TranslationKey.Description), flex: 2},
    {field: 'contractType', headerName: t(TranslationKey['Contract type']), flex: 1},
  ]

  return {navigate, t, showCreateButton, rows, columns}
}
